import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  FlagIcon,
  GaugeIcon,
  Loader2Icon,
  LockIcon,
  RefreshCwIcon,
  RulerIcon,
  SaveIcon,
  Share2Icon,
  StarIcon,
  TargetIcon,
  TrophyIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { liveSupabase } from "../lib/liveSupabaseClient";

const clampScore = (value) => {
  if (value === "" || value == null) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.min(30, Math.max(1, parsed));
};

const scoreKey = (unitId, holeNumber) =>
  `${unitId}-${holeNumber}`;

const entryKey = (configId, ballId) => `${configId}-${ballId}`;

const copyTextToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!copied) {
    throw new Error("Länken kunde inte kopieras.");
  }
};

const parseDistance = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
};

const formatDistance = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "–";
  return `${number.toLocaleString("sv-SE", { maximumFractionDigits: 2 })} m`;
};

const formatHandicap = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return Number.isInteger(number)
    ? String(number)
    : number.toFixed(1);
};

const formatRelativeToPar = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return "–";
  if (number === 0) return "E";
  return number > 0 ? `+${number}` : String(number);
};

const getHoleResultLabel = (strokes, par) => {
  if (strokes == null || strokes === "" || par == null || par === "") {
    return "Ej spelat";
  }

  const score = Number(strokes);
  const holePar = Number(par);

  if (!Number.isFinite(score) || !Number.isFinite(holePar)) return "Ej spelat";

  const difference = score - holePar;
  if (difference <= -3) return "Albatross eller bättre";
  if (difference === -2) return "Eagle";
  if (difference === -1) return "Birdie";
  if (difference === 0) return "Par";
  if (difference === 1) return "Bogey";
  return "Dubbelbogey eller sämre";
};

const getHoleScoreMarkerClass = (strokes, par) => {
  if (strokes == null || strokes === "" || par == null || par === "") {
    return "text-[#9aa09b]";
  }

  const score = Number(strokes);
  const holePar = Number(par);

  if (!Number.isFinite(score) || !Number.isFinite(holePar)) {
    return "text-[#9aa09b]";
  }

  const difference = score - holePar;

  if (difference <= -2) {
    return "rounded-full ring-2 ring-[#d6ad35] text-[#7a5a0a]";
  }

  if (difference === -1) {
    return "rounded-full ring-2 ring-[#bd4d49] text-[#9f3532]";
  }

  if (difference === 1) {
    return "rounded-[4px] bg-[#dcecf5] text-[#24465d]";
  }

  if (difference >= 2) {
    return "rounded-[4px] bg-[#315b79] text-white";
  }

  return "text-[#1f2d26]";
};

export default function LiveScorecard() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState("");
  const [pageMessage, setPageMessage] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [balls, setBalls] = useState([]);
  const [units, setUnits] = useState([]);
  const [ballUnits, setBallUnits] = useState([]);
  const [scores, setScores] = useState([]);
  const [liveRoundMeta, setLiveRoundMeta] = useState(null);
  const [miniGameHoles, setMiniGameHoles] = useState([]);
  const [miniGameEntries, setMiniGameEntries] = useState([]);
  const [miniGameAwards, setMiniGameAwards] = useState([]);
  const [previousPlayerStats, setPreviousPlayerStats] = useState({});
  const [miniGameDrafts, setMiniGameDrafts] = useState({});
  const [miniGameSaving, setMiniGameSaving] = useState({});
  const [leaderboardScope, setLeaderboardScope] = useState("round");
  const [includeMiniGamesInRanking, setIncludeMiniGamesInRanking] = useState(false);
  const [expandedLeaderboardUnitId, setExpandedLeaderboardUnitId] = useState(null);
  const [currentHole, setCurrentHole] = useState(1);
  const [activeView, setActiveView] =
    useState("scorecard");
  const [saveStates, setSaveStates] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] =
    useState(false);
  const [isRefreshing, setIsRefreshing] =
    useState(false);
  const [isSharingPublicLink, setIsSharingPublicLink] =
    useState(false);

  const timersRef = useRef(new Map());
  const pendingRef = useRef(new Map());
  const inviteInfoRef = useRef(null);
  const initialisePromiseRef = useRef(null);
  const accessRecoveryPromiseRef = useRef(null);

  const holes = inviteInfo?.holes ?? 18;
  const ownBallId = inviteInfo?.ball_id;

  const holePars = useMemo(() => {
    const values = Array.isArray(liveRoundMeta?.hole_pars)
      ? liveRoundMeta.hole_pars.map((value) => Number(value))
      : [];

    const valid =
      values.length === holes &&
      values.every(
        (value) => Number.isInteger(value) && value >= 2 && value <= 7
      );

    return valid ? values : null;
  }, [holes, liveRoundMeta?.hole_pars]);

  const unitBallMap = useMemo(() => {
    const map = {};

    ballUnits.forEach((relation) => {
      map[relation.scoring_unit_id] =
        relation.live_ball_id;
    });

    return map;
  }, [ballUnits]);

  const ballNumberMap = useMemo(() => {
    const map = {};

    balls.forEach((ball) => {
      map[ball.id] = ball.ball_number;
    });

    return map;
  }, [balls]);

  const ownUnits = useMemo(
    () =>
      units.filter(
        (unit) =>
          unitBallMap[unit.id] === ownBallId
      ),
    [units, unitBallMap, ownBallId]
  );

  const ownBall = useMemo(
    () =>
      balls.find((ball) => ball.id === ownBallId),
    [balls, ownBallId]
  );

  const isLocked =
    (liveRoundMeta?.status || inviteInfo?.live_round_status) !== "active" ||
    ownBall?.status === "submitted";

  const ensureLiveSession = useCallback(async () => {
    const {
      data: { session },
      error: sessionError,
    } = await liveSupabase.auth.getSession();

    if (sessionError) {
      throw sessionError;
    }

    if (session?.access_token) {
      return session;
    }

    const { data, error } =
      await liveSupabase.auth.signInAnonymously();

    if (error) {
      throw error;
    }

    if (!data?.session?.access_token) {
      throw new Error(
        "Kunde inte skapa en giltig session för live-scorekortet."
      );
    }

    return data.session;
  }, []);

  const claimInviteAccess = useCallback(async () => {
    if (!token) {
      throw new Error(
        "Scorekortslänken saknar en giltig token."
      );
    }

    await ensureLiveSession();

    const { data, error } = await liveSupabase.rpc(
      "claim_live_invite",
      {
        p_token: token,
      }
    );

    if (error) {
      throw error;
    }

    inviteInfoRef.current = data;
    setInviteInfo(data);
    return data;
  }, [ensureLiveSession, token]);

  const recoverInviteAccess = useCallback(async () => {
    if (accessRecoveryPromiseRef.current) {
      return accessRecoveryPromiseRef.current;
    }

    const recoveryPromise = claimInviteAccess();
    accessRecoveryPromiseRef.current = recoveryPromise;

    try {
      return await recoveryPromise;
    } finally {
      if (accessRecoveryPromiseRef.current === recoveryPromise) {
        accessRecoveryPromiseRef.current = null;
      }
    }
  }, [claimInviteAccess]);

  const refreshPreviousPlayerStats = useCallback(
    async (liveRoundId) => {
      if (!liveRoundId) {
        return false;
      }

      const { data, error } = await liveSupabase.rpc(
        "get_live_previous_player_stats",
        {
          p_live_round_id: liveRoundId,
        }
      );

      if (error) {
        console.warn(
          "Kunde inte hämta aktuella totaler från tidigare rundor:",
          error
        );
        return false;
      }

      setPreviousPlayerStats(
        data && typeof data === "object" ? data : {}
      );
      return true;
    },
    []
  );

  const scoreMap = useMemo(() => {
    const map = {};

    scores.forEach((score) => {
      map[
        scoreKey(
          score.scoring_unit_id,
          score.hole_number
        )
      ] = score.strokes;
    });

    return map;
  }, [scores]);

  const miniGameEntryMap = useMemo(
    () =>
      Object.fromEntries(
        miniGameEntries.map((entry) => [
          entryKey(entry.live_mini_game_hole_id, entry.live_ball_id),
          entry,
        ])
      ),
    [miniGameEntries]
  );

  const miniGameAwardMap = useMemo(
    () =>
      Object.fromEntries(
        miniGameAwards.map((award) => [
          award.live_mini_game_hole_id,
          award,
        ])
      ),
    [miniGameAwards]
  );

  const miniGameConfigMap = useMemo(
    () => Object.fromEntries(miniGameHoles.map((config) => [config.id, config])),
    [miniGameHoles]
  );

  const miniGamesByHole = useMemo(() => {
    const grouped = {};

    miniGameHoles.forEach((config) => {
      const holeNumber = Number(config.hole_number);
      if (!grouped[holeNumber]) grouped[holeNumber] = [];
      grouped[holeNumber].push(config);
    });

    return grouped;
  }, [miniGameHoles]);

  const miniGamesForCurrentHole = useMemo(
    () => miniGamesByHole[currentHole] || [],
    [currentHole, miniGamesByHole]
  );

  const currentHolePar = holePars?.[currentHole - 1] ?? null;

  const quickScoreOptions = useMemo(() => {
    const par = Number(currentHolePar);

    if (!Number.isFinite(par)) {
      return [3, 4, 5, 6, 7, 8, 9, 10];
    }

    const firstScore = Math.max(1, Math.round(par) - 2);

    return Array.from(
      { length: 8 },
      (_, index) => firstScore + index
    );
  }, [currentHolePar]);

  const ownPlayerOptions = useMemo(() => {
    const players = new Map();

    ownUnits.forEach((unit) => {
      (unit.player_ids || []).forEach((playerId) => {
        const name = unit.player_names?.[playerId] ||
          (unit.unit_type === "player" ? unit.label : `Spelare ${players.size + 1}`);
        players.set(playerId, { id: playerId, name });
      });
    });

    return [...players.values()];
  }, [ownUnits]);

  const updateScoreRowLocally = useCallback(
    (unitId, holeNumber, value) => {
      setScores((current) => {
        const existingIndex = current.findIndex(
          (score) =>
            score.scoring_unit_id === unitId &&
            score.hole_number === holeNumber
        );

        if (value == null) {
          if (existingIndex === -1) {
            return current;
          }

          return current.filter(
            (_, index) => index !== existingIndex
          );
        }

        if (existingIndex === -1) {
          return [
            ...current,
            {
              id: `local-${unitId}-${holeNumber}`,
              live_round_id:
                inviteInfo?.live_round_id,
              scoring_unit_id: unitId,
              hole_number: holeNumber,
              strokes: value,
              updated_at:
                new Date().toISOString(),
            },
          ];
        }

        return current.map((score, index) =>
          index === existingIndex
            ? {
                ...score,
                strokes: value,
                updated_at:
                  new Date().toISOString(),
              }
            : score
        );
      });
    },
    [inviteInfo?.live_round_id]
  );

  const persistScore = useCallback(
    async (unitId, holeNumber, value) => {
      const key = scoreKey(unitId, holeNumber);

      setSaveStates((current) => ({
        ...current,
        [key]: "saving",
      }));

      const writeScore = async (activeInvite) => {
        if (!activeInvite?.live_round_id) {
          throw new Error(
            "Live-scorekortet saknar giltig rundinformation."
          );
        }

        if (value == null) {
          const { error } = await liveSupabase
            .from("live_hole_scores")
            .delete()
            .eq("scoring_unit_id", unitId)
            .eq("hole_number", holeNumber);

          if (error) {
            throw error;
          }

          return null;
        }

        const { data, error } = await liveSupabase
          .from("live_hole_scores")
          .upsert(
            {
              live_round_id: activeInvite.live_round_id,
              scoring_unit_id: unitId,
              hole_number: holeNumber,
              strokes: value,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: "scoring_unit_id,hole_number",
            }
          )
          .select()
          .single();

        if (error) {
          throw error;
        }

        return data;
      };

      try {
        let activeInvite = inviteInfoRef.current;
        let savedScore;

        try {
          if (!activeInvite?.live_round_id) {
            activeInvite = await recoverInviteAccess();
          }

          savedScore = await writeScore(activeInvite);
        } catch (firstError) {
          console.warn(
            "Första sparförsöket misslyckades. Återställer åtkomsten och försöker igen:",
            firstError
          );

          activeInvite = await recoverInviteAccess();
          savedScore = await writeScore(activeInvite);
        }

        if (value == null) {
          setScores((current) =>
            current.filter(
              (score) =>
                !(
                  score.scoring_unit_id === unitId &&
                  score.hole_number === holeNumber
                )
            )
          );
        } else if (savedScore) {
          setScores((current) => {
            const withoutSame = current.filter(
              (score) =>
                !(
                  score.scoring_unit_id === unitId &&
                  score.hole_number === holeNumber
                )
            );

            return [...withoutSame, savedScore];
          });
        }

        setSaveStates((current) => ({
          ...current,
          [key]: "saved",
        }));

        window.setTimeout(() => {
          setSaveStates((current) => {
            if (current[key] !== "saved") {
              return current;
            }

            const updated = { ...current };
            delete updated[key];
            return updated;
          });
        }, 1400);
      } catch (error) {
        console.error(
          "Kunde inte autospara resultat efter ett nytt försök:",
          error
        );

        setSaveStates((current) => ({
          ...current,
          [key]: "error",
        }));
        setPageMessage(
          "Resultatet kunde inte sparas. Din inmatning ligger kvar på skärmen – tryck på samma resultat igen eller uppdatera sidan."
        );
        throw error;
      } finally {
        pendingRef.current.delete(key);
        timersRef.current.delete(key);
      }
    },
    [recoverInviteAccess]
  );

  const scheduleScoreSave = useCallback(
    (unitId, holeNumber, rawValue, immediate = false) => {
      if (isLocked) {
        return;
      }

      const value = clampScore(rawValue);
      const key = scoreKey(unitId, holeNumber);

      updateScoreRowLocally(
        unitId,
        holeNumber,
        value
      );

      pendingRef.current.set(key, {
        unitId,
        holeNumber,
        value,
      });

      const existingTimer =
        timersRef.current.get(key);

      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      if (immediate) {
        persistScore(
          unitId,
          holeNumber,
          value
        ).catch(() => {});
        return;
      }

      const timer = window.setTimeout(() => {
        const pending =
          pendingRef.current.get(key);

        if (!pending) {
          return;
        }

        persistScore(
          pending.unitId,
          pending.holeNumber,
          pending.value
        ).catch(() => {});
      }, 450);

      timersRef.current.set(key, timer);
    },
    [
      isLocked,
      persistScore,
      updateScoreRowLocally,
    ]
  );

  const flushPendingScores = useCallback(async () => {
    const pendingItems = [
      ...pendingRef.current.values(),
    ];

    timersRef.current.forEach((timer) => {
      window.clearTimeout(timer);
    });
    timersRef.current.clear();

    await Promise.all(
      pendingItems.map((pending) =>
        persistScore(
          pending.unitId,
          pending.holeNumber,
          pending.value
        )
      )
    );
  }, [persistScore]);

  const fetchLiveData = useCallback(
    async (claimedInvite) => {
      const liveRoundId = claimedInvite.live_round_id;

      const [
        liveRoundResponse,
        ballsResponse,
        unitsResponse,
        scoresResponse,
        miniGameHolesResponse,
      ] = await Promise.all([
        liveSupabase
          .from("live_rounds")
          .select("id, status, round_count, round_index, holes, hole_pars, team_mode, team_data")
          .eq("id", liveRoundId)
          .single(),
        liveSupabase
          .from("live_balls")
          .select("id, live_round_id, ball_number, status, submitted_at")
          .eq("live_round_id", liveRoundId)
          .order("ball_number", { ascending: true }),
        liveSupabase
          .from("live_scoring_units")
          .select(
            "id, live_round_id, unit_type, label, player_ids, player_names, player_previous_totals, sort_order, full_handicap, round_deduction, previous_total"
          )
          .eq("live_round_id", liveRoundId)
          .order("sort_order", { ascending: true }),
        liveSupabase
          .from("live_hole_scores")
          .select("id, live_round_id, scoring_unit_id, hole_number, strokes, updated_at")
          .eq("live_round_id", liveRoundId),
        liveSupabase
          .from("live_mini_game_holes")
          .select(
            "id, live_round_id, mini_game_id, game_type, mini_game_name, subtract_from_score, deduction_value, hole_number"
          )
          .eq("live_round_id", liveRoundId)
          .order("hole_number", { ascending: true }),
      ]);

      for (const response of [
        liveRoundResponse,
        ballsResponse,
        unitsResponse,
        scoresResponse,
        miniGameHolesResponse,
      ]) {
        if (response.error) throw response.error;
      }

      const ballIds = (ballsResponse.data || []).map((ball) => ball.id);
      const configIds = (miniGameHolesResponse.data || []).map((config) => config.id);

      const [relationsResponse, entriesResponse, awardsResponse] = await Promise.all([
        ballIds.length
          ? liveSupabase
              .from("live_ball_units")
              .select("live_ball_id, scoring_unit_id")
              .in("live_ball_id", ballIds)
          : Promise.resolve({ data: [], error: null }),
        configIds.length
          ? liveSupabase
              .from("live_mini_game_entries")
              .select(
                "id, live_mini_game_hole_id, live_ball_id, player_id, distance_meters, no_valid_result, updated_at"
              )
              .in("live_mini_game_hole_id", configIds)
          : Promise.resolve({ data: [], error: null }),
        configIds.length
          ? liveSupabase
              .from("live_mini_game_awards")
              .select(
                "id, live_mini_game_hole_id, player_id, distance_meters, finalized_at, updated_at"
              )
              .in("live_mini_game_hole_id", configIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      for (const response of [relationsResponse, entriesResponse, awardsResponse]) {
        if (response.error) throw response.error;
      }

      const relations = relationsResponse.data || [];
      const entries = entriesResponse.data || [];

      setLiveRoundMeta(liveRoundResponse.data);
      setBalls(ballsResponse.data || []);
      setUnits(unitsResponse.data || []);
      setBallUnits(relations);
      setScores(scoresResponse.data || []);
      setMiniGameHoles(miniGameHolesResponse.data || []);
      setMiniGameEntries(entries);
      setMiniGameAwards(awardsResponse.data || []);
      setMiniGameDrafts((current) => {
        const next = { ...current };
        entries
          .filter((entry) => entry.live_ball_id === claimedInvite.ball_id)
          .forEach((entry) => {
            next[entry.live_mini_game_hole_id] = {
              playerId: entry.player_id || "",
              distance: entry.distance_meters == null ? "" : String(entry.distance_meters),
              noValidResult: entry.no_valid_result,
            };
          });
        return next;
      });

      const ownUnitIds = relations
        .filter((relation) => relation.live_ball_id === claimedInvite.ball_id)
        .map((relation) => relation.scoring_unit_id);

      const loadedScoreMap = Object.fromEntries(
        (scoresResponse.data || []).map((score) => [
          scoreKey(score.scoring_unit_id, score.hole_number),
          score.strokes,
        ])
      );

      let firstIncompleteHole = 1;
      for (let holeNumber = 1; holeNumber <= claimedInvite.holes; holeNumber += 1) {
        const complete = ownUnitIds.every(
          (unitId) => loadedScoreMap[scoreKey(unitId, holeNumber)] > 0
        );
        if (!complete) {
          firstIncompleteHole = holeNumber;
          break;
        }
        firstIncompleteHole = claimedInvite.holes;
      }

      setCurrentHole(firstIncompleteHole);
      await refreshPreviousPlayerStats(liveRoundId);
    },
    [refreshPreviousPlayerStats]
  );

  const initialise = useCallback(async () => {
    if (initialisePromiseRef.current) {
      return initialisePromiseRef.current;
    }

    const initialisePromise = (async () => {
      if (!token) {
        setFatalError(
          "Scorekortslänken saknar en giltig token."
        );
        setLoading(false);
        return;
      }

      setLoading(true);
      setFatalError("");

      try {
        const claimedInvite = await claimInviteAccess();
        await fetchLiveData(claimedInvite);
      } catch (error) {
        console.error(
          "Kunde inte öppna live-scorekortet:",
          error
        );
        setFatalError(
          error.message ||
            "Scorekortslänken kunde inte öppnas."
        );
      } finally {
        setLoading(false);
      }
    })();

    initialisePromiseRef.current = initialisePromise;

    try {
      return await initialisePromise;
    } finally {
      if (initialisePromiseRef.current === initialisePromise) {
        initialisePromiseRef.current = null;
      }
    }
  }, [claimInviteAccess, fetchLiveData, token]);

  useEffect(() => {
    initialise();
  }, [initialise]);

  useEffect(() => {
    if (!inviteInfo?.live_round_id) return undefined;

    const liveRoundId = inviteInfo.live_round_id;
    const upsertById = (setter, changed) =>
      setter((current) => [
        ...current.filter((item) => item.id !== changed.id),
        changed,
      ]);

    const channel = liveSupabase
      .channel(`live-scorecard-${liveRoundId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "live_hole_scores", filter: `live_round_id=eq.${liveRoundId}` },
        (payload) => {
          if (payload.eventType === "DELETE") {
            setScores((current) => current.filter((score) => score.id !== payload.old.id));
            return;
          }
          const changed = payload.new;
          setScores((current) => [
            ...current.filter(
              (score) =>
                score.id !== changed.id &&
                !(
                  score.scoring_unit_id === changed.scoring_unit_id &&
                  score.hole_number === changed.hole_number
                )
            ),
            changed,
          ]);
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "live_balls", filter: `live_round_id=eq.${liveRoundId}` },
        (payload) => {
          setBalls((current) => current.map((ball) => (ball.id === payload.new.id ? payload.new : ball)));
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "live_rounds", filter: `id=eq.${liveRoundId}` },
        (payload) => setLiveRoundMeta(payload.new)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "live_mini_game_entries" },
        (payload) => {
          const configId = payload.new?.live_mini_game_hole_id || payload.old?.live_mini_game_hole_id;
          if (!miniGameHoles.some((config) => config.id === configId)) return;
          if (payload.eventType === "DELETE") {
            setMiniGameEntries((current) => current.filter((entry) => entry.id !== payload.old.id));
          } else {
            upsertById(setMiniGameEntries, payload.new);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "live_mini_game_awards" },
        (payload) => {
          const configId = payload.new?.live_mini_game_hole_id || payload.old?.live_mini_game_hole_id;
          if (!miniGameHoles.some((config) => config.id === configId)) return;
          if (payload.eventType === "DELETE") {
            setMiniGameAwards((current) => current.filter((award) => award.id !== payload.old.id));
          } else {
            upsertById(setMiniGameAwards, payload.new);
          }
        }
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setPageMessage(
            "Live-anslutningen tappades. Tryck på uppdatera för att hämta senaste ställningen."
          );
        }
      });

    return () => {
      liveSupabase.removeChannel(channel);
    };
  }, [inviteInfo?.live_round_id, miniGameHoles]);

  useEffect(() => {
    if (leaderboardScope !== "round") {
      setExpandedLeaderboardUnitId(null);
    }
  }, [leaderboardScope]);

  useEffect(() => {
    if (
      leaderboardScope !== "total" ||
      !inviteInfo?.live_round_id
    ) {
      return undefined;
    }

    let cancelled = false;

    const updatePreviousTotals = async () => {
      if (cancelled) return;
      await refreshPreviousPlayerStats(
        inviteInfo.live_round_id
      );
    };

    updatePreviousTotals();

    const intervalId = window.setInterval(
      updatePreviousTotals,
      15000
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updatePreviousTotals();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [
    inviteInfo?.live_round_id,
    leaderboardScope,
    refreshPreviousPlayerStats,
  ]);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
    },
    []
  );

  const holeIsComplete = useCallback(
    (holeNumber) =>
      ownUnits.length > 0 &&
      ownUnits.every(
        (unit) =>
          scoreMap[
            scoreKey(unit.id, holeNumber)
          ] > 0
      ),
    [ownUnits, scoreMap]
  );

  const miniGamesAnswered = useMemo(
    () =>
      miniGameHoles.every(
        (config) => miniGameEntryMap[entryKey(config.id, ownBallId)]
      ),
    [miniGameEntryMap, miniGameHoles, ownBallId]
  );

  const saveMiniGameEntry = async (config, noValidResult = false) => {
    const draft = miniGameDrafts[config.id] || {};
    const distance = parseDistance(draft.distance);

    if (!noValidResult && (!draft.playerId || distance == null)) {
      setPageMessage("Välj spelare och ange en positiv distans i meter.");
      return;
    }

    setMiniGameSaving((current) => ({ ...current, [config.id]: true }));
    setPageMessage("");

    try {
      const { error } = await liveSupabase.rpc("save_live_mini_game_entry", {
        p_live_mini_game_hole_id: config.id,
        p_live_ball_id: ownBallId,
        p_player_id: noValidResult ? null : draft.playerId,
        p_distance_meters: noValidResult ? null : distance,
        p_no_valid_result: noValidResult,
      });

      if (error) throw error;

      const optimistic = {
        id: miniGameEntryMap[entryKey(config.id, ownBallId)]?.id ||
          `local-${config.id}-${ownBallId}`,
        live_mini_game_hole_id: config.id,
        live_ball_id: ownBallId,
        player_id: noValidResult ? null : draft.playerId,
        distance_meters: noValidResult ? null : distance,
        no_valid_result: noValidResult,
        updated_at: new Date().toISOString(),
      };

      setMiniGameEntries((current) => [
        ...current.filter(
          (entry) =>
            !(
              entry.live_mini_game_hole_id === config.id &&
              entry.live_ball_id === ownBallId
            )
        ),
        optimistic,
      ]);
      setMiniGameDrafts((current) => ({
        ...current,
        [config.id]: {
          playerId: noValidResult ? "" : draft.playerId,
          distance: noValidResult ? "" : String(distance),
          noValidResult,
        },
      }));
    } catch (error) {
      console.error("Kunde inte spara resultatet för sidotävlingen:", error);
      setPageMessage(error.message || "Kunde inte spara resultatet för sidotävlingen.");
    } finally {
      setMiniGameSaving((current) => ({ ...current, [config.id]: false }));
    }
  };

  const completedHoles = useMemo(() => {
    let count = 0;

    for (
      let holeNumber = 1;
      holeNumber <= holes;
      holeNumber += 1
    ) {
      if (holeIsComplete(holeNumber)) {
        count += 1;
      }
    }

    return count;
  }, [holeIsComplete, holes]);

  const scorecardComplete =
    ownUnits.length > 0 &&
    completedHoles === holes;

  const missingScoreItems = useMemo(() => {
    const missing = [];

    ownUnits.forEach((unit) => {
      for (let holeNumber = 1; holeNumber <= holes; holeNumber += 1) {
        const value = scoreMap[scoreKey(unit.id, holeNumber)];

        if (!(Number(value) > 0)) {
          missing.push({
            unitId: unit.id,
            unitLabel: unit.label,
            holeNumber,
          });
        }
      }
    });

    return missing;
  }, [holes, ownUnits, scoreMap]);

  const missingMiniGameItems = useMemo(
    () =>
      miniGameHoles
        .filter(
          (config) =>
            !miniGameEntryMap[
              entryKey(config.id, ownBallId)
            ]
        )
        .map((config) => ({
          id: config.id,
          name: config.mini_game_name,
          holeNumber: Number(config.hole_number),
        })),
    [miniGameEntryMap, miniGameHoles, ownBallId]
  );

  const currentHoleMissingScores = useMemo(
    () =>
      missingScoreItems.filter(
        (item) => item.holeNumber === currentHole
      ),
    [currentHole, missingScoreItems]
  );

  const currentHoleMissingMiniGames = useMemo(
    () =>
      missingMiniGameItems.filter(
        (item) => item.holeNumber === currentHole
      ),
    [currentHole, missingMiniGameItems]
  );

  const currentHoleHasAnyScore = useMemo(
    () =>
      ownUnits.some(
        (unit) =>
          Number(
            scoreMap[scoreKey(unit.id, currentHole)]
          ) > 0
      ),
    [currentHole, ownUnits, scoreMap]
  );

  const currentHoleNeedsAttention =
    (currentHoleHasAnyScore &&
      currentHoleMissingScores.length > 0) ||
    (holeIsComplete(currentHole) &&
      currentHoleMissingMiniGames.length > 0);

  const goToFirstMissing = useCallback(() => {
    const firstScore = missingScoreItems[0];
    const firstMiniGame = missingMiniGameItems[0];

    let targetHole = null;

    if (firstScore && firstMiniGame) {
      targetHole = Math.min(
        firstScore.holeNumber,
        firstMiniGame.holeNumber
      );
    } else {
      targetHole =
        firstScore?.holeNumber ??
        firstMiniGame?.holeNumber ??
        null;
    }

    if (targetHole != null) {
      setCurrentHole(targetHole);
      setActiveView("scorecard");
      setPageMessage("");
    }
  }, [missingMiniGameItems, missingScoreItems]);

  const moveToNextHole = useCallback(() => {
    const warningParts = [];

    if (currentHoleMissingScores.length > 0) {
      warningParts.push(
        `resultat saknas för ${currentHoleMissingScores
          .map((item) => item.unitLabel)
          .join(", ")}`
      );
    }

    if (currentHoleMissingMiniGames.length > 0) {
      warningParts.push(
        `sidotävling saknas: ${currentHoleMissingMiniGames
          .map((item) => item.name)
          .join(", ")}`
      );
    }

    if (warningParts.length > 0) {
      setPageMessage(
        `Kontrollera hål ${currentHole}: ${warningParts.join(
          " · "
        )}. Du kan fortsätta och komplettera senare.`
      );
    } else {
      setPageMessage("");
    }

    setCurrentHole((current) =>
      Math.min(holes, current + 1)
    );
  }, [
    currentHole,
    currentHoleMissingMiniGames,
    currentHoleMissingScores,
    holes,
  ]);

  const leaderboardRows = useMemo(() => {
    const anyHandicapDeductions = units.some(
      (unit) => Number(unit.round_deduction) > 0
    );
    const competitionTeamData =
      liveRoundMeta?.team_data || {};
    const competitionTeamAssignments =
      competitionTeamData.assignments || {};

    const getCompetitionTeam = (playerIds = []) => {
      if (
        !liveRoundMeta?.team_mode ||
        Object.keys(competitionTeamAssignments).length === 0
      ) {
        return null;
      }

      const teamKeys = [
        ...new Set(
          playerIds
            .map(
              (playerId) =>
                competitionTeamAssignments[
                  String(playerId)
                ] ??
                competitionTeamAssignments[playerId]
            )
            .filter(Boolean)
        ),
      ];

      if (teamKeys.length !== 1) {
        return null;
      }

      const teamKey = teamKeys[0];

      return {
        key: teamKey,
        name:
          competitionTeamData.captains?.[teamKey] ||
          (teamKey === "red" ? "Röd" : "Grön"),
        color:
          competitionTeamData.colors?.[teamKey] ||
          (teamKey === "red" ? "#9F3A38" : "#2F6B4F"),
      };
    };

    const getAwardDeduction = (awards) =>
      (awards || []).reduce((total, award) => {
        const config =
          miniGameConfigMap[
            award.live_mini_game_hole_id
          ];

        return (
          total +
          (config?.subtract_from_score
            ? Number(config.deduction_value || 0)
            : 0)
        );
      }, 0);

    const roundRows = units.map((unit) => {
      const unitScores = scores.filter(
        (score) => score.scoring_unit_id === unit.id
      );
      const gross = unitScores.reduce(
        (sum, score) =>
          sum + Number(score.strokes || 0),
        0
      );
      const holesPlayed = new Set(
        unitScores
          .filter((score) => score.strokes > 0)
          .map((score) => score.hole_number)
      ).size;
      const handicapDeduction = anyHandicapDeductions
        ? Number(unit.round_deduction || 0)
        : 0;

      const unitAwards = miniGameAwards.filter(
        (award) =>
          (unit.player_ids || []).some(
            (playerId) =>
              String(playerId) ===
              String(award.player_id)
          )
      );
      const miniGameDeduction =
        getAwardDeduction(unitAwards);
      const appliedMiniGameDeduction =
        includeMiniGamesInRanking &&
        unit.unit_type !== "team"
          ? miniGameDeduction
          : 0;
      const starCount = unitAwards.length;
      const baseRoundScore =
        gross - handicapDeduction;
      const roundScore =
        baseRoundScore - appliedMiniGameDeduction;
      const roundScoreWithStars =
        baseRoundScore - miniGameDeduction;
      const holeScores = Array.from(
        { length: holes },
        (_, index) =>
          scoreMap[
            scoreKey(unit.id, index + 1)
          ] || null
      );
      const parPlayed = holePars
        ? unitScores.reduce((sum, score) => {
            const par = Number(
              holePars[
                Number(score.hole_number) - 1
              ]
            );
            return (
              sum +
              (Number.isFinite(par) ? par : 0)
            );
          }, 0)
        : null;
      const relativeToPar = holePars
        ? gross -
          parPlayed -
          handicapDeduction -
          appliedMiniGameDeduction
        : null;
      const relativeToParWithStars = holePars
        ? gross -
          parPlayed -
          handicapDeduction -
          miniGameDeduction
        : null;
      const roundSortScore =
        relativeToPar == null
          ? roundScore
          : relativeToPar;

      return {
        ...unit,
        ballId: unitBallMap[unit.id],
        ballNumber:
          ballNumberMap[unitBallMap[unit.id]] ||
          "–",
        gross,
        holesPlayed,
        baseRoundScore,
        roundScore,
        roundScoreWithStars,
        holeScores,
        parPlayed,
        relativeToPar,
        relativeToParWithStars,
        roundSortScore,
        displayScore:
          relativeToPar == null
            ? roundScore
            : formatRelativeToPar(relativeToPar),
        handicapDeduction,
        miniGameDeduction,
        appliedMiniGameDeduction,
        starCount,
        unitAwards,
        competitionTeam: getCompetitionTeam(
          unit.player_ids || []
        ),
        isOwn:
          unitBallMap[unit.id] === ownBallId,
      };
    });

    roundRows.sort((a, b) => {
      const aScore = Number.isFinite(a.roundSortScore)
        ? a.roundSortScore
        : Number.POSITIVE_INFINITY;
      const bScore = Number.isFinite(b.roundSortScore)
        ? b.roundSortScore
        : Number.POSITIVE_INFINITY;

      if (aScore !== bScore) {
        return aScore - bScore;
      }

      const sortOrderDifference =
        Number(a.sort_order || 0) -
        Number(b.sort_order || 0);

      if (sortOrderDifference !== 0) {
        return sortOrderDifference;
      }

      return String(a.label || "").localeCompare(
        String(b.label || ""),
        "sv-SE"
      );
    });

    const totalRowsByPlayer = new Map();

    roundRows.forEach((row) => {
      (row.player_ids || []).forEach(
        (playerId) => {
          const playerKey = String(playerId);
          const currentAwards =
            row.unitAwards.filter(
              (award) =>
                String(award.player_id) ===
                playerKey
            );
          const currentMiniGameDeduction =
            getAwardDeduction(currentAwards);
          const currentRoundScore =
            row.baseRoundScore -
            currentMiniGameDeduction;

          const fetchedPreviousStats =
            previousPlayerStats?.[playerKey];
          const fallbackPreviousTotal = Number(
            row.player_previous_totals?.[
              playerKey
            ] ??
              row.player_previous_totals?.[
                playerId
              ] ??
              row.previous_total ??
              0
          );
          const previousTotal =
            fetchedPreviousStats &&
            typeof fetchedPreviousStats === "object"
              ? Number(
                  fetchedPreviousStats.total ??
                    fallbackPreviousTotal
                )
              : fallbackPreviousTotal;
          const previousRawTotal =
            fetchedPreviousStats &&
            typeof fetchedPreviousStats === "object"
              ? Number(
                  fetchedPreviousStats.raw_total ??
                    previousTotal
                )
              : previousTotal;
          const previousMiniGameDeduction =
            fetchedPreviousStats &&
            typeof fetchedPreviousStats === "object"
              ? Number(
                  fetchedPreviousStats.mini_game_deduction ||
                    0
                )
              : 0;
          const previousStarCount =
            fetchedPreviousStats &&
            typeof fetchedPreviousStats === "object"
              ? Number(
                  fetchedPreviousStats.star_count || 0
                )
              : 0;
          const totalScore =
            previousTotal + currentRoundScore;
          const playerName =
            row.player_names?.[playerKey] ??
            row.player_names?.[playerId] ??
            (row.unit_type === "player"
              ? row.label
              : "Okänd spelare");

          totalRowsByPlayer.set(playerKey, {
            ...row,
            id: `total-player-${playerKey}`,
            sourceUnitId: row.id,
            unit_type: "player_total",
            label: playerName,
            player_ids: [playerId],
            previousTotal,
            previousRawTotal,
            previousMiniGameDeduction,
            currentRoundScore,
            currentRoundMiniGameDeduction: currentMiniGameDeduction,
            totalScore,
            displayScore: totalScore,
            miniGameDeduction:
              previousMiniGameDeduction +
              currentMiniGameDeduction,
            appliedMiniGameDeduction:
              currentMiniGameDeduction,
            starCount:
              previousStarCount +
              currentAwards.length,
            currentRoundStarCount:
              currentAwards.length,
            unitAwards: currentAwards,
            competitionTeam:
              getCompetitionTeam([playerId]),
          });
        }
      );
    });

    const totalRows = [
      ...totalRowsByPlayer.values(),
    ].sort((a, b) => {
      if (a.totalScore !== b.totalScore) {
        return a.totalScore - b.totalScore;
      }

      return String(a.label || "").localeCompare(
        String(b.label || ""),
        "sv-SE"
      );
    });

    const rows =
      leaderboardScope === "total"
        ? totalRows
        : roundRows;
    const roundHasIndividualUnits =
      roundRows.some(
        (row) => row.unit_type === "player"
      );
    const hasMiniGameDeductions =
      miniGameHoles.some(
        (config) => config.subtract_from_score
      );
    const roundAnyDeductions =
      anyHandicapDeductions ||
      (includeMiniGamesInRanking &&
        roundHasIndividualUnits &&
        hasMiniGameDeductions);
    const totalAnyDeductions =
      anyHandicapDeductions ||
      totalRows.some(
        (row) => row.miniGameDeduction > 0
      );

    return {
      rows,
      roundRows,
      totalRows,
      hasPars: Boolean(holePars),
      hasMiniGameDeductions,
      hasIndividualUnits:
        roundHasIndividualUnits,
      anyDeductions:
        leaderboardScope === "total"
          ? totalAnyDeductions
          : roundAnyDeductions,
    };
  }, [
    ballNumberMap,
    holePars,
    holes,
    includeMiniGamesInRanking,
    leaderboardScope,
    liveRoundMeta?.team_data,
    liveRoundMeta?.team_mode,
    miniGameAwards,
    miniGameConfigMap,
    miniGameHoles,
    ownBallId,
    previousPlayerStats,
    scoreMap,
    scores,
    unitBallMap,
    units,
  ]);

  const teamLeaderboardRows = useMemo(() => {
    const teamData = liveRoundMeta?.team_data || {};
    const assignments = teamData.assignments || {};

    if (!liveRoundMeta?.team_mode || Object.keys(assignments).length === 0) {
      return [];
    }

    const configuredKeys = ["red", "green"].filter((teamKey) =>
      Object.values(assignments).includes(teamKey)
    );

    return configuredKeys
      .map((teamKey) => {
        let roundTotal = 0;
        let previousTotal = 0;
        let miniGameDeduction = 0;
        const playerIds = new Set();
        let holesPlayed = 0;

        leaderboardRows.roundRows.forEach((row) => {
          const teamPlayerIds = (row.player_ids || []).filter(
            (playerId) => assignments[playerId] === teamKey
          );

          if (teamPlayerIds.length === 0) return;

          holesPlayed = Math.max(holesPlayed, row.holesPlayed);

          teamPlayerIds.forEach((playerId) => {
            playerIds.add(playerId);

            // Scramblelagets egen score påverkas inte av stjärnor, men i den
            // stora lagtävlingen följer varje spelares individuella mini game-avdrag med.
            const playerStarDeduction = miniGameAwards.reduce((total, award) => {
              if (String(award.player_id) !== String(playerId)) return total;

              const config = miniGameConfigMap[award.live_mini_game_hole_id];
              return (
                total +
                (config?.subtract_from_score
                  ? Number(config.deduction_value || 0)
                  : 0)
              );
            }, 0);

            roundTotal +=
              row.baseRoundScore - playerStarDeduction;
            miniGameDeduction += playerStarDeduction;
            const fetchedPreviousStats =
              previousPlayerStats?.[String(playerId)];

            previousTotal += Number(
              fetchedPreviousStats &&
              typeof fetchedPreviousStats === "object"
                ? fetchedPreviousStats.total ??
                    row.player_previous_totals?.[String(playerId)] ??
                    row.player_previous_totals?.[playerId] ??
                    row.previous_total ??
                    0
                : row.player_previous_totals?.[String(playerId)] ??
                    row.player_previous_totals?.[playerId] ??
                    row.previous_total ??
                    0
            );
          });
        });

        return {
          key: teamKey,
          name:
            teamData.captains?.[teamKey] ||
            (teamKey === "red" ? "Röd" : "Grön"),
          color:
            teamData.colors?.[teamKey] ||
            (teamKey === "red" ? "#9F3A38" : "#2F6B4F"),
          playerCount: playerIds.size,
          holesPlayed,
          roundTotal,
          miniGameDeduction,
          total: previousTotal + roundTotal,
        };
      })
      .sort((a, b) => {
        const aScore = leaderboardScope === "total" ? a.total : a.roundTotal;
        const bScore = leaderboardScope === "total" ? b.total : b.roundTotal;
        return aScore - bScore;
      });
  }, [
    leaderboardRows.roundRows,
    leaderboardScope,
    liveRoundMeta?.team_data,
    liveRoundMeta?.team_mode,
    miniGameAwards,
    miniGameConfigMap,
    previousPlayerStats,
  ]);

  const renderCompactScorecard = (holeScores) => {
    const segments = [
      {
        key: "front",
        label: holeScores.length > 9 ? "Front 9" : "Hål 1–9",
        start: 0,
        end: Math.min(9, holeScores.length),
      },
      ...(holeScores.length > 9
        ? [{ key: "back", label: "Back 9", start: 9, end: holeScores.length }]
        : []),
    ];

    return (
      <div className="space-y-3">
        {segments.map((segment) => {
          const segmentScores = holeScores.slice(segment.start, segment.end);
          const segmentPars = holePars?.slice(segment.start, segment.end) || null;
          const segmentGross = segmentScores.reduce(
            (sum, score) => sum + Number(score || 0),
            0
          );
          const played = segmentScores.filter((score) => score != null).length;

          return (
            <div key={segment.key}>
              <div className="mb-1.5 flex items-center justify-between px-0.5">
                <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#68716c]">
                  {segment.label}
                </span>
                {played > 0 && (
                  <span className="text-[9px] font-medium tabular-nums text-[#68716c]">
                    {segmentGross} slag
                  </span>
                )}
              </div>

              <div className="overflow-hidden rounded-lg border border-[#d8d2c4]">
                <table className="w-full table-fixed border-collapse text-[9px] sm:text-[10px]">
                  <colgroup>
                    <col style={{ width: "34px" }} />
                    {segmentScores.map((_, index) => (
                      <col key={index} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr className="bg-[#183d2d] text-white">
                      <th className="px-0.5 py-2 text-center text-[8px] font-semibold uppercase tracking-[0.08em]">
                        Hål
                      </th>
                      {segmentScores.map((_, index) => (
                        <th
                          key={segment.start + index + 1}
                          className="border-l border-white/10 px-0.5 py-2 text-center font-semibold"
                        >
                          {segment.start + index + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {segmentPars && (
                      <tr className="border-b border-[#e4ded1] bg-[#f7f4ec]">
                        <th className="px-0.5 py-1.5 text-center text-[8px] font-semibold uppercase tracking-[0.08em] text-[#68716c]">
                          Par
                        </th>
                        {segmentPars.map((par, index) => (
                          <td
                            key={index}
                            className="border-l border-[#e4ded1] px-0.5 py-1.5 text-center font-medium tabular-nums text-[#68716c]"
                          >
                            {par}
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr className="bg-[#fffdf8]">
                      <th className="px-0.5 py-2 text-center text-[8px] font-semibold uppercase tracking-[0.08em] text-[#68716c]">
                        Slag
                      </th>
                      {segmentScores.map((score, index) => {
                        const absoluteIndex = segment.start + index;
                        const par = holePars?.[absoluteIndex];

                        return (
                          <td
                            key={absoluteIndex}
                            className="border-l border-[#e4ded1] px-0.5 py-2 text-center"
                            title={getHoleResultLabel(score, par)}
                          >
                            <span
                              className={`inline-flex h-5 min-w-5 items-center justify-center px-0.5 text-[10px] font-semibold tabular-nums ${getHoleScoreMarkerClass(
                                score,
                                par
                              )}`}
                            >
                              {score ?? "–"}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const ownReviewRows = useMemo(
    () =>
      ownUnits.map((unit) => {
        const holeScores = Array.from(
          { length: holes },
          (_, index) =>
            scoreMap[
              scoreKey(unit.id, index + 1)
            ] || null
        );
        const gross = holeScores.reduce(
          (sum, score) => sum + (score || 0),
          0
        );

        return {
          ...unit,
          holeScores,
          gross,
          finalScore: Math.max(
            0,
            gross -
              Number(unit.round_deduction || 0)
          ),
        };
      }),
    [holes, ownUnits, scoreMap]
  );

  const changeScore = (
    unitId,
    holeNumber,
    difference
  ) => {
    const current =
      scoreMap[scoreKey(unitId, holeNumber)] || 0;
    const next =
      current === 0 && difference > 0
        ? 1
        : current + difference;

    if (next < 1) {
      scheduleScoreSave(
        unitId,
        holeNumber,
        null,
        true
      );
      return;
    }

    scheduleScoreSave(
      unitId,
      holeNumber,
      Math.min(30, next),
      true
    );
  };

  const openReview = async () => {
    setPageMessage("");

    try {
      await flushPendingScores();
    } catch {
      return;
    }

    if (missingScoreItems.length > 0) {
      const firstMissing = missingScoreItems[0];
      const missingOnFirstHole = missingScoreItems.filter(
        (item) =>
          item.holeNumber === firstMissing.holeNumber
      );

      setPageMessage(
        `Scorekortet är inte komplett. Resultat saknas på ${
          missingScoreItems.length
        } ${missingScoreItems.length === 1 ? "ställe" : "ställen"}. ` +
          `På hål ${firstMissing.holeNumber}: ${missingOnFirstHole
            .map((item) => item.unitLabel)
            .join(", ")}.`
      );
      setCurrentHole(firstMissing.holeNumber);
      setActiveView("scorecard");
      return;
    }

    if (!miniGamesAnswered) {
      const firstMissing = missingMiniGameItems[0];

      setPageMessage(
        `Kunde inte spara resultatet för sidotävlingen: ${missingMiniGameItems
          .map(
            (item) =>
              `${item.name} på hål ${item.holeNumber}`
          )
          .join(", ")}. Registrera ett resultat eller välj ingen giltig träff.`
      );
      setCurrentHole(firstMissing.holeNumber);
      setActiveView("scorecard");
      return;
    }

    setReviewOpen(true);
  };

  const submitBall = async () => {
    setIsSubmitting(true);
    setPageMessage("");

    try {
      await flushPendingScores();

      const { data, error } =
        await liveSupabase.rpc(
          "submit_live_ball_with_mini_games",
          {
            p_ball_id: ownBallId,
          }
        );

      if (error) {
        throw error;
      }

      setBalls((current) =>
        current.map((ball) =>
          ball.id === ownBallId
            ? {
                ...ball,
                status: "submitted",
                submitted_at:
                  new Date().toISOString(),
              }
            : ball
        )
      );

      if (data.live_round_completed) {
        setLiveRoundMeta((current) => ({ ...current, status: "completed" }));
      }

      setReviewOpen(false);
      setPageMessage(
        data.live_round_completed
          ? "Scorekortet är inskickat och hela rundan är avslutad."
          : "Scorekortet är inskickat och sparat i turneringen."
      );
    } catch (error) {
      console.error(
        "Kunde inte skicka in scorekortet:",
        error
      );
      setPageMessage(
        error.message ||
          "Scorekortet kunde inte skickas in."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const refresh = async () => {
    if (!inviteInfo) {
      return;
    }

    setIsRefreshing(true);
    setPageMessage("");

    try {
      await fetchLiveData(inviteInfo);
    } catch (error) {
      console.error(
        "Kunde inte uppdatera scorekortet:",
        error
      );
      setPageMessage(
        "Senaste resultaten kunde inte hämtas."
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const sharePublicViewerLink = async () => {
    if (!token || isSharingPublicLink) {
      return;
    }

    setIsSharingPublicLink(true);
    setPageMessage("");

    try {
      const { data, error } = await liveSupabase.rpc(
        "create_or_get_live_round_public_link_by_invite",
        {
          p_invite_token: token,
        }
      );

      if (error) {
        throw error;
      }

      if (!data?.token) {
        throw new Error(
          "Tittarlänken kunde inte skapas."
        );
      }

      const viewerUrl =
        `${window.location.origin}/live-resultat/${data.token}`;
      const shareData = {
        title: `${
          inviteInfo?.tournament_name || "HoleInOne.se"
        } – liveresultat`,
        text: `Följ ${
          inviteInfo?.tournament_name || "golftävlingen"
        } live`,
        url: viewerUrl,
      };

      if (typeof navigator.share === "function") {
        try {
          await navigator.share(shareData);
          setPageMessage(
            "Den skrivskyddade tittarlänken har delats."
          );
          return;
        } catch (shareError) {
          if (shareError?.name === "AbortError") {
            return;
          }

          console.warn(
            "Systemets delningsdialog kunde inte användas. Kopierar länken i stället:",
            shareError
          );
        }
      }

      await copyTextToClipboard(viewerUrl);
      setPageMessage(
        "Den skrivskyddade tittarlänken har kopierats."
      );
    } catch (error) {
      console.error(
        "Kunde inte dela tittarlänken:",
        error
      );
      setPageMessage(
        error?.message ||
          "Tittarlänken kunde inte delas."
      );
    } finally {
      setIsSharingPublicLink(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 shadow-sm">
          <Loader2Icon className="h-5 w-5 animate-spin text-[#183d2d]" />
          <div>
            <p className="text-sm font-semibold text-[#1f2d26]">
              Öppnar live-scorekortet
            </p>
            <p className="text-xs text-[#68716c]">
              Kontrollerar länken och hämtar rundan…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (fatalError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <section className="w-full max-w-md rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] p-6 text-center shadow-sm">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#f3e8e5] text-[#a0443e]">
            <AlertCircleIcon className="h-6 w-6" />
          </span>
          <h1 className="mt-4 text-xl font-semibold text-[#183d2d]">
            Länken kunde inte öppnas
          </h1>
          <p className="mt-2 text-sm leading-6 text-[#68716c]">
            {fatalError}
          </p>
          <button
            type="button"
            onClick={initialise}
            className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#214d39]"
          >
            <RefreshCwIcon className="h-4 w-4" />
            Försök igen
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f0e7] pb-24 text-[#1f2d26]">
      <header className="relative overflow-hidden border-b border-black/10 bg-[#183d2d] text-[#fffdf8]">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 h-60 w-60 rounded-full bg-white/10 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 pb-5 pt-5 sm:px-6 sm:pb-7">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/55">
                HoleInOne.se · Live
              </p>
              <h1 className="mt-1 truncate text-xl font-semibold sm:text-2xl">
                {inviteInfo?.tournament_name}
              </h1>
              <p className="mt-1 truncate text-sm text-white/65">
                {inviteInfo?.round_name} · Boll{" "}
                {inviteInfo?.ball_number}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={sharePublicViewerLink}
                disabled={isSharingPublicLink}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[#b08b45] px-3.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#be9a56] disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Dela skrivskyddad tittarlänk"
              >
                {isSharingPublicLink ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <Share2Icon className="h-4 w-4" />
                )}
                <span>Dela</span>
              </button>

              <button
                type="button"
                onClick={refresh}
                disabled={isRefreshing}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 disabled:opacity-60"
                aria-label="Uppdatera"
              >
                <RefreshCwIcon
                  className={`h-4 w-4 ${
                    isRefreshing
                      ? "animate-spin"
                      : ""
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium">
              {holes} hål
            </span>
            <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium">
              {ownUnits.length}{" "}
              {ownUnits.length === 1
                ? "score"
                : "scorer"}
            </span>
            <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium">
              {completedHoles}/{holes} klara
            </span>
            {isLocked && (
              <span className="rounded-full bg-[#f2ead9] px-3 py-1.5 text-xs font-semibold text-[#9a7735]">
                Låst
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-3 sm:px-5 lg:px-6">
        <nav className="relative z-10 -mt-3">
          <div className="grid grid-cols-2 gap-1 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] p-1.5 shadow-[0_8px_24px_rgba(31,45,38,0.10)]">
            <button
              type="button"
              onClick={() =>
                setActiveView("scorecard")
              }
              className={`rounded-xl px-3 py-2.5 text-xs font-semibold transition ${
                activeView === "scorecard"
                  ? "bg-[#183d2d] text-white shadow-sm"
                  : "text-[#68716c] hover:bg-[#f7f4ec]"
              }`}
            >
              Scorekort
            </button>
            <button
              type="button"
              onClick={() =>
                setActiveView("leaderboard")
              }
              className={`rounded-xl px-3 py-2.5 text-xs font-semibold transition ${
                activeView === "leaderboard"
                  ? "bg-[#183d2d] text-white shadow-sm"
                  : "text-[#68716c] hover:bg-[#f7f4ec]"
              }`}
            >
              Live-leaderboard
            </button>
          </div>
        </nav>

        {pageMessage && (
          <div
            className="mt-4 flex items-start gap-3 rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-3.5 py-3 text-sm text-[#1f2d26]"
            role="status"
          >
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7735]" />
            <p className="leading-5">
              {pageMessage}
            </p>
          </div>
        )}

        {activeView === "scorecard" && (
          <div className="mt-4 space-y-4">
            <section className="overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
              <div
                className={`border-b border-[#d8d2c4] px-4 py-3 ${
                  miniGamesForCurrentHole.length > 0
                    ? "bg-[#fff8e8]"
                    : "bg-[#f7f4ec]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#68716c]">
                      Aktuellt hål
                    </p>
                    <h2 className="mt-0.5 text-lg font-semibold text-[#183d2d]">
                      Hål {currentHole} av {holes}
                    </h2>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {currentHolePar != null && (
                        <span className="rounded-full border border-[#cfc8b9] bg-[#fffdf8] px-2.5 py-1 text-[10px] font-semibold text-[#183d2d]">
                          Par {currentHolePar}
                        </span>
                      )}

                      {miniGamesForCurrentHole.map((config) => (
                        <span
                          key={config.id}
                          className="inline-flex items-center gap-1 rounded-full border border-[#e4d3aa] bg-[#fffaf0] px-2.5 py-1 text-[10px] font-semibold text-[#8b692b]"
                        >
                          <StarIcon className="h-3 w-3 fill-current" />
                          {config.mini_game_name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#183d2d] text-base font-semibold text-white">
                    {currentHole}
                    {miniGamesForCurrentHole.length > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-[#fff8e8] bg-[#b08b45] text-white">
                        <StarIcon className="h-2.5 w-2.5 fill-current" />
                      </span>
                    )}
                  </span>
                </div>

                {miniGamesForCurrentHole.length > 0 && (
                  <p className="mt-2 text-[10px] leading-4 text-[#8b692b]">
                    Sidotävling på detta hål – registrera resultatet efter att bollen spelat klart hålet.
                  </p>
                )}
              </div>

              <div className="flex gap-2 overflow-x-auto px-3 py-3">
                {Array.from(
                  { length: holes },
                  (_, index) => index + 1
                ).map((holeNumber) => {
                  const complete = holeIsComplete(holeNumber);
                  const active = currentHole === holeNumber;
                  const holeMiniGames = miniGamesByHole[holeNumber] || [];
                  const hasMiniGame = holeMiniGames.length > 0;
                  const miniGameNames = holeMiniGames
                    .map((config) => config.mini_game_name)
                    .join(", ");

                  return (
                    <button
                      key={holeNumber}
                      type="button"
                      onClick={() => setCurrentHole(holeNumber)}
                      className={`relative flex h-10 min-w-10 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition ${
                        active
                          ? "border-[#183d2d] bg-[#183d2d] text-white shadow-sm"
                          : complete
                            ? "border-[#bfd5c6] bg-[#e5ede7] text-[#287148]"
                            : hasMiniGame
                              ? "border-[#d8bf82] bg-[#fff8e8] text-[#8b692b]"
                              : "border-[#d8d2c4] bg-[#fffdf8] text-[#68716c]"
                      }`}
                      aria-label={`Hål ${holeNumber}${
                        currentHolePar != null && currentHole === holeNumber
                          ? `, par ${currentHolePar}`
                          : ""
                      }${hasMiniGame ? `, sidotävling: ${miniGameNames}` : ""}${
                        complete ? ", klart" : ""
                      }`}
                      title={hasMiniGame ? `${miniGameNames} på hål ${holeNumber}` : undefined}
                    >
                      <span>{holeNumber}</span>

                      {hasMiniGame && (
                        <span
                          className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full border text-[8px] ${
                            active
                              ? "border-[#183d2d] bg-[#b08b45] text-white"
                              : "border-[#fffdf8] bg-[#b08b45] text-white"
                          }`}
                          aria-hidden="true"
                        >
                          <StarIcon className="h-2.5 w-2.5 fill-current" />
                        </span>
                      )}

                      {complete && (
                        <span
                          className={`absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border ${
                            active
                              ? "border-[#183d2d] bg-white text-[#287148]"
                              : "border-[#fffdf8] bg-[#287148] text-white"
                          }`}
                          aria-hidden="true"
                        >
                          <CheckCircle2Icon className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="grid gap-3 sm:grid-cols-2">
              {ownUnits.map((unit) => {
                const key = scoreKey(
                  unit.id,
                  currentHole
                );
                const value =
                  scoreMap[key] ?? "";
                const saveState =
                  saveStates[key];

                return (
                  <section
                    key={unit.id}
                    className="rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#68716c]">
                          {unit.unit_type === "team"
                            ? "Lag"
                            : "Spelare"}
                        </p>
                        <h3 className="mt-0.5 break-words text-base font-semibold text-[#1f2d26]">
                          {unit.label}
                        </h3>
                      </div>

                      {Number(
                        unit.round_deduction
                      ) > 0 && (
                        <span className="shrink-0 rounded-full bg-[#e5ede7] px-2.5 py-1 text-[9px] font-semibold text-[#183d2d]">
                          Avdrag{" "}
                          {unit.round_deduction}
                        </span>
                      )}
                    </div>

                    <div className="mt-5 grid grid-cols-[52px_minmax(0,1fr)_52px] items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          changeScore(
                            unit.id,
                            currentHole,
                            -1
                          )
                        }
                        disabled={isLocked}
                        className="flex h-12 items-center justify-center rounded-xl border border-[#d8d2c4] bg-[#f7f4ec] text-[#183d2d] transition hover:bg-[#e5ede7] disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`Minska resultatet för ${unit.label}`}
                      >
                        <ChevronDownIcon className="h-5 w-5" />
                      </button>

                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="30"
                        value={value}
                        disabled={isLocked}
                        onFocus={(event) =>
                          event.currentTarget.select()
                        }
                        onChange={(event) =>
                          scheduleScoreSave(
                            unit.id,
                            currentHole,
                            event.target.value
                          )
                        }
                        onBlur={(event) =>
                          scheduleScoreSave(
                            unit.id,
                            currentHole,
                            event.target.value,
                            true
                          )
                        }
                        className="h-16 w-full rounded-2xl border border-[#b8b09f] bg-[#fffdf8] px-3 text-center text-3xl font-semibold tabular-nums text-[#183d2d] outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/20 disabled:cursor-not-allowed disabled:bg-[#f7f4ec]"
                        aria-label={`Antal slag för ${unit.label} på hål ${currentHole}`}
                        placeholder="–"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          changeScore(
                            unit.id,
                            currentHole,
                            1
                          )
                        }
                        disabled={isLocked}
                        className="flex h-12 items-center justify-center rounded-xl bg-[#183d2d] text-white transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`Öka resultatet för ${unit.label}`}
                      >
                        <ChevronUpIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {!isLocked && (
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        {quickScoreOptions.map(
                          (quickScore) => (
                            <button
                              key={quickScore}
                              type="button"
                              onClick={() =>
                                scheduleScoreSave(
                                  unit.id,
                                  currentHole,
                                  quickScore,
                                  true
                                )
                              }
                              className={`rounded-xl border px-2 py-2 text-xs font-semibold transition ${
                                value === quickScore
                                  ? "border-[#183d2d] bg-[#e5ede7] text-[#183d2d]"
                                  : "border-[#d8d2c4] bg-[#fffdf8] text-[#68716c] hover:bg-[#f7f4ec]"
                              }`}
                            >
                              {quickScore}
                            </button>
                          )
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex min-h-4 items-center justify-between gap-3">
                      <p className="text-[10px] text-[#68716c]">
                        {Number(
                          unit.full_handicap
                        ) > 0
                          ? `HCP ${formatHandicap(
                              unit.full_handicap
                            )}`
                          : "Bruttoscore"}
                      </p>

                      {saveState === "saving" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#68716c]">
                          <Loader2Icon className="h-3 w-3 animate-spin" />
                          Sparar
                        </span>
                      )}

                      {saveState === "saved" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#287148]">
                          <CheckCircle2Icon className="h-3 w-3" />
                          Sparat
                        </span>
                      )}

                      {saveState === "error" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#a0443e]">
                          <AlertCircleIcon className="h-3 w-3" />
                          Ej sparat
                        </span>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>

            {miniGamesForCurrentHole.length > 0 && (
              <section className="overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
                <div className="border-b border-[#d8d2c4] bg-[#f7f4ec] px-4 py-3">
                  <div className="flex items-start gap-3">
                    <StarIcon className="mt-0.5 h-5 w-5 fill-[#b08b45] text-[#b08b45]" />
                    <div>
                      <h2 className="text-sm font-semibold text-[#183d2d]">
                        Sidotävlingar på hål {currentHole}
                      </h2>
                      <p className="mt-1 text-xs leading-5 text-[#68716c]">
                        Registrera bollens bästa giltiga resultat. Vinnaren fastställs när alla bollar har spelat hålet och lämnat sitt svar.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4">
                  {miniGamesForCurrentHole.map((config) => {
                    const draft = miniGameDrafts[config.id] || { playerId: "", distance: "" };
                    const ownEntry = miniGameEntryMap[entryKey(config.id, ownBallId)];
                    const award = miniGameAwardMap[config.id];
                    const winnerName = units
                      .flatMap((unit) =>
                        (unit.player_ids || []).map((playerId) => ({
                          id: playerId,
                          name: unit.player_names?.[playerId] ||
                            (unit.unit_type === "player" ? unit.label : "Spelare"),
                        }))
                      )
                      .find((player) => player.id === award?.player_id)?.name;
                    const saving = miniGameSaving[config.id];

                    return (
                      <article key={config.id} className="rounded-xl border border-[#d8d2c4] bg-[#fffdf8] p-3.5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-[#1f2d26]">
                              {config.mini_game_name}
                            </p>
                            <p className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[#68716c]">
                              {config.game_type === "closest_to_pin" ? (
                                <TargetIcon className="h-3 w-3" />
                              ) : (
                                <RulerIcon className="h-3 w-3" />
                              )}
                              {config.game_type === "closest_to_pin"
                                ? "Kortast distans vinner"
                                : "Längst distans vinner"}
                            </p>
                          </div>

                          {award ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#f2ead9] px-2.5 py-1 text-[9px] font-semibold text-[#9a7735]">
                              <StarIcon className="h-3 w-3 fill-current" />
                              Avgjord
                            </span>
                          ) : ownEntry ? (
                            <span className="rounded-full bg-[#e5ede7] px-2.5 py-1 text-[9px] font-semibold text-[#287148]">
                              Registrerat
                            </span>
                          ) : null}
                        </div>

                        {award && (
                          <div className="mt-3 rounded-xl border border-[#e4d3aa] bg-[#fffaf0] p-3">
                            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9a7735]">
                              Nuvarande vinnare
                            </p>
                            <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#1f2d26]">
                              <StarIcon className="h-4 w-4 fill-[#b08b45] text-[#b08b45]" />
                              {winnerName || "Spelare"} · {formatDistance(award.distance_meters)}
                            </p>
                          </div>
                        )}

                        {ownEntry?.no_valid_result && (
                          <p className="mt-3 rounded-xl bg-[#f7f4ec] px-3 py-2 text-xs text-[#68716c]">
                            Din boll har registrerat ingen giltig träff.
                          </p>
                        )}

                        <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_140px]">
                          <select
                            value={draft.playerId || ""}
                            disabled={isLocked || saving}
                            onChange={(event) =>
                              setMiniGameDrafts((current) => ({
                                ...current,
                                [config.id]: {
                                  ...current[config.id],
                                  playerId: event.target.value,
                                  noValidResult: false,
                                },
                              }))
                            }
                            className="h-11 rounded-xl border border-[#b8b09f] bg-[#fffdf8] px-3 text-sm text-[#1f2d26] outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/20 disabled:bg-[#f7f4ec]"
                          >
                            <option value="">Välj spelare</option>
                            {ownPlayerOptions.map((player) => (
                              <option key={player.id} value={player.id}>
                                {player.name}
                              </option>
                            ))}
                          </select>

                          <div className="relative">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.distance || ""}
                              disabled={isLocked || saving}
                              onChange={(event) =>
                                setMiniGameDrafts((current) => ({
                                  ...current,
                                  [config.id]: {
                                    ...current[config.id],
                                    distance: event.target.value,
                                    noValidResult: false,
                                  },
                                }))
                              }
                              placeholder="Distans"
                              className="h-11 w-full rounded-xl border border-[#b8b09f] bg-[#fffdf8] px-3 pr-9 text-sm tabular-nums text-[#1f2d26] outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/20 disabled:bg-[#f7f4ec]"
                            />
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#68716c]">m</span>
                          </div>
                        </div>

                        {!isLocked && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => saveMiniGameEntry(config, false)}
                              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-3 text-xs font-semibold text-white transition hover:bg-[#214d39] disabled:opacity-50"
                            >
                              {saving ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SaveIcon className="h-4 w-4" />}
                              Spara distans
                            </button>
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => saveMiniGameEntry(config, true)}
                              className="min-h-10 rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-3 text-xs font-semibold text-[#68716c] transition hover:bg-[#f7f4ec] disabled:opacity-50"
                            >
                              Ingen giltig träff
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {!isLocked && currentHoleNeedsAttention && (
              <section className="flex items-start gap-3 rounded-2xl border border-[#e4d3aa] bg-[#fff8e8] px-4 py-3">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7735]" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#6f531f]">
                    Kontrollera hål {currentHole}
                  </p>
                  <div className="mt-1 space-y-0.5 text-[11px] leading-5 text-[#7d6841]">
                    {currentHoleMissingScores.length > 0 && (
                      <p>
                        Resultat saknas för{" "}
                        <span className="font-semibold">
                          {currentHoleMissingScores
                            .map((item) => item.unitLabel)
                            .join(", ")}
                        </span>
                        .
                      </p>
                    )}
                    {currentHoleMissingMiniGames.length > 0 && (
                      <p>
                        Sidotävling saknas:{" "}
                        <span className="font-semibold">
                          {currentHoleMissingMiniGames
                            .map((item) => item.name)
                            .join(", ")}
                        </span>
                        .
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {isLocked && (
              <section className="flex items-start gap-3 rounded-2xl border border-[#d8d2c4] bg-[#f7f4ec] p-4">
                <LockIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#9a7735]" />
                <div>
                  <h3 className="text-sm font-semibold text-[#183d2d]">
                    Scorekortet är låst
                  </h3>
                  <p className="mt-1 text-xs leading-5 text-[#68716c]">
                    Bollen är redan inskickad eller så
                    har administratören avslutat rundan.
                    Resultaten går fortfarande att se i
                    leaderboarden.
                  </p>
                </div>
              </section>
            )}

            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <button
                type="button"
                disabled={currentHole === 1}
                onClick={() =>
                  setCurrentHole((current) =>
                    Math.max(1, current - 1)
                  )
                }
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-3 text-xs font-semibold text-[#68716c] transition hover:bg-[#f7f4ec] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Föregående
              </button>

              <span className="text-xs font-medium text-[#68716c]">
                {currentHole}/{holes}
              </span>

              {currentHole < holes ? (
                <button
                  type="button"
                  onClick={moveToNextHole}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-3 text-xs font-semibold text-white transition hover:bg-[#214d39]"
                >
                  Nästa
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={openReview}
                  disabled={isLocked}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-3 text-xs font-semibold text-white transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FlagIcon className="h-4 w-4" />
                  Granska
                </button>
              )}
            </div>

            {!isLocked &&
              (currentHole === holes || scorecardComplete) && (
                <section
                  className={`rounded-2xl border px-4 py-3 ${
                    missingScoreItems.length === 0 &&
                    missingMiniGameItems.length === 0
                      ? "border-[#bfd5c6] bg-[#eef5f0]"
                      : "border-[#e4d3aa] bg-[#fff8e8]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {missingScoreItems.length === 0 &&
                    missingMiniGameItems.length === 0 ? (
                      <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#287148]" />
                    ) : (
                      <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7735]" />
                    )}

                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-xs font-semibold ${
                          missingScoreItems.length === 0 &&
                          missingMiniGameItems.length === 0
                            ? "text-[#287148]"
                            : "text-[#6f531f]"
                        }`}
                      >
                        {missingScoreItems.length === 0 &&
                        missingMiniGameItems.length === 0
                          ? "Allt är ifyllt"
                          : "Slutkontrollen hittade luckor"}
                      </p>

                      {missingScoreItems.length === 0 &&
                      missingMiniGameItems.length === 0 ? (
                        <p className="mt-0.5 text-[11px] leading-5 text-[#68716c]">
                          Samtliga scorer och sidotävlingar är registrerade.
                        </p>
                      ) : (
                        <p className="mt-0.5 text-[11px] leading-5 text-[#7d6841]">
                          {missingScoreItems.length > 0 &&
                            `${missingScoreItems.length} resultat saknas`}
                          {missingScoreItems.length > 0 &&
                            missingMiniGameItems.length > 0 &&
                            " · "}
                          {missingMiniGameItems.length > 0 &&
                            `${missingMiniGameItems.length} sidotävlings-svar saknas`}
                        </p>
                      )}
                    </div>

                    {(missingScoreItems.length > 0 ||
                      missingMiniGameItems.length > 0) && (
                      <button
                        type="button"
                        onClick={goToFirstMissing}
                        className="shrink-0 rounded-lg border border-[#d8bf82] bg-[#fffdf8] px-2.5 py-1.5 text-[10px] font-semibold text-[#7a5a0a] transition hover:bg-white"
                      >
                        Visa
                      </button>
                    )}
                  </div>
                </section>
              )}

            {!isLocked && (
              <button
                type="button"
                onClick={openReview}
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214d39]"
              >
                <SaveIcon className="h-4 w-4" />
                Granska och skicka in
              </button>
            )}
          </div>
        )}

        {activeView === "leaderboard" && (
          <>
          <section className="mt-4 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
            <div className="border-b border-[#d8d2c4] px-4 py-4 sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#68716c]">
                    Live från alla bollar
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-[#183d2d]">
                    Leaderboard
                  </h2>
                </div>

                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                  <TrophyIcon className="h-4 w-4" />
                </span>
              </div>

              <p className="mt-2 text-xs leading-5 text-[#68716c]">
                När par är registrerat visas rundresultatet mot banan. Under Pågående runda kan stjärnor väljas in i en individuell rundranking. Under Totalt räknas stjärnorna alltid individuellt, precis som i turneringsställningen. Scramblelagets eget rondresultat får inget stjärnavdrag. Tryck på en spelare eller ett scramblelag under Pågående runda för att öppna hela scorekortet.
              </p>

              {Number(liveRoundMeta?.round_count || 1) > 1 && (
                <div className="mt-3 grid grid-cols-2 gap-1 rounded-xl bg-[#f7f4ec] p-1">
                  <button
                    type="button"
                    onClick={() => setLeaderboardScope("round")}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                      leaderboardScope === "round"
                        ? "bg-[#183d2d] text-white shadow-sm"
                        : "text-[#68716c]"
                    }`}
                  >
                    Pågående runda
                  </button>
                  <button
                    type="button"
                    onClick={() => setLeaderboardScope("total")}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                      leaderboardScope === "total"
                        ? "bg-[#183d2d] text-white shadow-sm"
                        : "text-[#68716c]"
                    }`}
                  >
                    Totalt
                  </button>
                </div>
              )}

              {leaderboardScope === "round" &&
                leaderboardRows.hasMiniGameDeductions &&
                leaderboardRows.hasIndividualUnits && (
                <div className="mt-3 flex items-center justify-end gap-2 text-[10px] text-[#68716c]">
                  <span>Stjärnor i individuell ranking</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={includeMiniGamesInRanking}
                    onClick={() =>
                      setIncludeMiniGamesInRanking((current) => !current)
                    }
                    className={`relative h-5 w-9 shrink-0 rounded-full border transition ${
                      includeMiniGamesInRanking
                        ? "border-[#183d2d] bg-[#183d2d]"
                        : "border-[#c8c1b3] bg-[#e8e4da]"
                    }`}
                    title={
                      includeMiniGamesInRanking
                        ? "Stjärnor räknas med i individuell ranking"
                        : "Stjärnor räknas inte med i individuell ranking"
                    }
                  >
                    <span
                      className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                        includeMiniGamesInRanking
                          ? "translate-x-[18px]"
                          : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              )}
            </div>



            <div className="divide-y divide-[#e4ded1]">
              {leaderboardRows.rows.map((row, index) => {
                const isExpanded =
                  leaderboardScope === "round" &&
                  expandedLeaderboardUnitId === row.id;
                const expandedMiniGameWins = (row.unitAwards || [])
                  .map((award) => {
                    const config =
                      miniGameConfigMap[
                        award.live_mini_game_hole_id
                      ];

                    if (!config) return null;

                    const playerKey = String(
                      award.player_id
                    );
                    const winnerName =
                      row.player_names?.[playerKey] ??
                      row.player_names?.[
                        award.player_id
                      ] ??
                      (row.unit_type === "player"
                        ? row.label
                        : "Okänd spelare");

                    return {
                      id:
                        award.id ||
                        `${award.live_mini_game_hole_id}-${playerKey}`,
                      winnerName,
                      gameName:
                        config.mini_game_name ||
                        "Sidotävling",
                      holeNumber: Number(
                        config.hole_number
                      ),
                      deduction:
                        config.subtract_from_score
                          ? Number(
                              config.deduction_value || 0
                            )
                          : 0,
                    };
                  })
                  .filter(Boolean)
                  .sort(
                    (a, b) =>
                      a.holeNumber - b.holeNumber ||
                      a.gameName.localeCompare(
                        b.gameName,
                        "sv-SE"
                      )
                  );

                return (
                  <div
                    key={row.id}
                    className={
                      index % 2 === 0
                        ? "bg-[#fffdf8]"
                        : "bg-[#f7f4ec]"
                    }
                    style={
                      row.competitionTeam
                        ? {
                            boxShadow: `inset 3px 0 0 ${row.competitionTeam.color}`,
                          }
                        : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (leaderboardScope !== "round") return;
                        setExpandedLeaderboardUnitId((current) =>
                          current === row.id ? null : row.id
                        );
                      }}
                      className={`grid w-full items-center gap-2 px-2.5 text-left sm:gap-3 sm:px-5 ${
                        leaderboardScope === "round"
                          ? "grid-cols-[34px_minmax(0,1fr)_48px_18px] py-3.5 cursor-pointer transition hover:bg-black/[0.025] sm:grid-cols-[38px_minmax(0,1fr)_64px_20px]"
                          : "grid-cols-[34px_minmax(0,1fr)_76px] py-3 sm:grid-cols-[38px_minmax(0,1fr)_86px]"
                      }`}
                      aria-expanded={isExpanded}
                    >
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                          index === 0
                            ? "bg-[#183d2d] text-white"
                            : "border border-[#d8d2c4] bg-[#fffdf8] text-[#68716c]"
                        }`}
                      >
                        {index + 1}
                      </span>

                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-semibold text-[#1f2d26]">
                          <span className="min-w-0 break-words leading-snug">
                            {row.label}
                          </span>
                          {row.starCount > 0 && (
                            <span
                              className="inline-flex shrink-0 items-center gap-px text-[#b08b45]"
                              title={`${row.starCount} sidotävlingsvinst${row.starCount === 1 ? "" : "er"}`}
                            >
                              {Array.from(
                                { length: Math.min(row.starCount, 4) },
                                (_, starIndex) => (
                                  <StarIcon
                                    key={starIndex}
                                    className="h-3.5 w-3.5 fill-current"
                                  />
                                )
                              )}
                              {row.starCount > 4 && (
                                <span className="ml-0.5 text-[9px] font-semibold">
                                  +{row.starCount - 4}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#68716c]">
                          {leaderboardScope === "total" ? (
                            <>
                              <span>Före rundan {row.previousTotal}</span>
                              <span>Pågående {row.currentRoundScore}</span>
                            </>
                          ) : (
                            <>
                              <span>{row.holesPlayed}/{holes} hål</span>
                              {leaderboardRows.hasPars &&
                                row.holesPlayed > 0 && (
                                  <span>· Brutto {row.gross}
                                  </span>
                                )}
                            </>
                          )}
                          {row.isOwn && (
                            <span className="inline-flex items-center rounded-full bg-[#e5ede7] px-2 py-0.5 text-[9px] font-semibold text-[#183d2d]">
                              Din boll
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="min-w-0 text-right">
                        <p className="text-xl font-semibold leading-none tabular-nums text-[#183d2d]">
                          {row.displayScore}
                        </p>
                        {(leaderboardScope === "total" || !leaderboardRows.hasPars) && (
                          <p className="mt-1 whitespace-nowrap text-[8px] font-medium uppercase tracking-[0.1em] text-[#68716c]">
                            {leaderboardScope === "total"
                              ? "Inkl. live"
                              : leaderboardRows.anyDeductions
                                ? "Prel. netto"
                                : "Slag"}
                          </p>
                        )}
                        {row.miniGameDeduction > 0 && (
                          <p className="mt-0.5 whitespace-nowrap text-[9px] font-semibold text-[#9a7735]">
                            {leaderboardScope === "total"
                              ? `★ −${row.miniGameDeduction}`
                              : row.unit_type === "team"
                                ? ""
                                : includeMiniGamesInRanking
                                  ? `★ −${row.miniGameDeduction}`
                                  : `★ −${row.miniGameDeduction} ej inräknat`}
                          </p>
                        )}
                      </div>

                      {leaderboardScope === "round" && (
                        <span className="flex w-[18px] justify-end sm:w-5">
                          {isExpanded ? (
                            <ChevronUpIcon className="h-4 w-4 shrink-0 text-[#9a7735]" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#9a7735]" />
                          )}
                        </span>
                      )}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-[#d8d2c4] bg-[#fffdf8] px-3 py-4 sm:px-5">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#68716c]">
                              Fullständigt scorekort
                            </p>
                            <h3 className="mt-1 text-sm font-semibold text-[#183d2d]">
                              {row.label}
                            </h3>
                          </div>
                          {leaderboardRows.hasPars && row.holesPlayed > 0 && (
                            <span className="rounded-full bg-[#e5ede7] px-3 py-1.5 text-xs font-semibold text-[#183d2d]">
                              {formatRelativeToPar(row.relativeToPar)}
                            </span>
                          )}
                        </div>

                        {renderCompactScorecard(row.holeScores)}

                        {expandedMiniGameWins.length > 0 && (
                          <div className="mt-3 border-t border-[#e4ded1] pt-3">
                            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#68716c]">
                              Sidotävlingsvinster
                            </p>
                            <div className="mt-1.5 space-y-1.5">
                              {expandedMiniGameWins.map((win) => (
                                <div
                                  key={win.id}
                                  className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-5 text-[#68716c]"
                                >
                                  <StarIcon className="h-3.5 w-3.5 shrink-0 fill-[#b08b45] text-[#b08b45]" />
                                  {row.unit_type === "team" && (
                                    <>
                                      <span className="font-semibold text-[#1f2d26]">
                                        {win.winnerName}
                                      </span>
                                      <span aria-hidden="true">·</span>
                                    </>
                                  )}
                                  <span>{win.gameName}</span>
                                  <span aria-hidden="true">·</span>
                                  <span>Hål {win.holeNumber}</span>
                                  {win.deduction > 0 && (
                                    <>
                                      <span aria-hidden="true">·</span>
                                      <span className="font-semibold text-[#287148]">
                                        −{win.deduction} slag
                                      </span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-[#68716c]">
                          <span className="rounded-full border border-[#d8d2c4] bg-[#f7f4ec] px-2.5 py-1">
                            Spelat {row.holesPlayed}/{holes}
                          </span>
                          <span className="rounded-full border border-[#d8d2c4] bg-[#f7f4ec] px-2.5 py-1">
                            Brutto {row.gross}
                          </span>
                          {row.handicapDeduction > 0 && (
                            <span className="rounded-full border border-[#c9ddcf] bg-[#eef6f0] px-2.5 py-1 text-[#287148]">
                              HCP −{row.handicapDeduction}
                            </span>
                          )}
                          {row.miniGameDeduction > 0 && (
                            <span className="rounded-full border border-[#dfcfab] bg-[#fbf4e3] px-2.5 py-1 text-[#8b692b]">
                              {row.unit_type === "team"
                                ? ""
                                : includeMiniGamesInRanking
                                  ? `Sidotävlingar −${row.miniGameDeduction}`
                                  : `★ −${row.miniGameDeduction} ej inräknat`}
                            </span>
                          )}
                        </div>


                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {leaderboardRows.rows.length === 0 && (
              <div className="px-4 py-10 text-center">
                <GaugeIcon className="mx-auto h-6 w-6 text-[#9a7735]" />
                <p className="mt-3 text-sm font-semibold text-[#183d2d]">
                  Inga resultat ännu
                </p>
                <p className="mt-1 text-xs text-[#68716c]">
                  Ställningen visas när den första bollen
                  börjar registrera scorer.
                </p>
              </div>
            )}
          </section>

          {teamLeaderboardRows.length > 0 && (
            <section className="mt-4 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
              <div className="flex items-end justify-between gap-3 border-b border-[#d8d2c4] bg-[#f7f4ec] px-4 py-3.5 sm:px-5">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#68716c]">
                    Lagtävling
                  </p>
                  <h3 className="mt-0.5 text-sm font-semibold text-[#183d2d]">
                    Lagets total
                  </h3>
                </div>
                <span className="shrink-0 text-[8px] font-medium uppercase tracking-[0.1em] text-[#68716c]">
                  {leaderboardScope === "total"
                    ? "Totalt inkl. live"
                    : "Pågående runda"}
                </span>
              </div>

              <div className="grid gap-2 p-3 sm:grid-cols-2 sm:p-4">
                {teamLeaderboardRows.map((team, teamIndex) => (
                  <div
                    key={team.key}
                    className="rounded-xl border border-[#d8d2c4] border-l-4 bg-[#fffdf8] px-3.5 py-3"
                    style={{ borderLeftColor: team.color }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#68716c]">
                          Placering {teamIndex + 1}
                        </p>
                        <p className="mt-1 truncate text-sm font-semibold text-[#1f2d26]">
                          Team {team.name}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[9px] text-[#68716c]">
                          <span>{team.playerCount} spelare</span>
                          <span>{team.holesPlayed}/{holes} hål</span>
                          {team.miniGameDeduction > 0 && (
                            <span className="font-semibold text-[#9a7735]">
                              ★ −{team.miniGameDeduction}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-xl font-semibold leading-none tabular-nums text-[#183d2d]">
                          {leaderboardScope === "total"
                            ? team.total
                            : team.roundTotal}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          </>
        )}
      </main>

      {reviewOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-end justify-center bg-black/55 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="review-title"
        >
          <div className="flex max-h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl bg-[#fffdf8] shadow-2xl sm:max-h-[90vh] sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4 bg-[#183d2d] px-4 py-4 text-white sm:px-6">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-white/60">
                  Slutkontroll
                </p>
                <h2
                  id="review-title"
                  className="mt-1 text-lg font-semibold sm:text-xl"
                >
                  Granska scorekortet
                </h2>
                <p className="mt-1 text-xs text-white/60">
                  Boll {inviteInfo?.ball_number} ·{" "}
                  {inviteInfo?.round_name}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setReviewOpen(false)
                }
                disabled={isSubmitting}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20 disabled:opacity-50"
                aria-label="Stäng granskningen"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 sm:p-5">

              <div className="space-y-3">
                {ownReviewRows.map((row) => (
                  <section
                    key={row.id}
                    className="rounded-xl border border-[#d8d2c4] bg-[#fffdf8] p-3"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#1f2d26]">
                          {row.label}
                        </p>
                        <p className="mt-0.5 text-[9px] text-[#68716c]">
                          Brutto {row.gross}
                        </p>
                      </div>
                      <span className="rounded-full bg-[#e5ede7] px-3 py-1.5 text-sm font-semibold tabular-nums text-[#183d2d]">
                        {row.finalScore}
                      </span>
                    </div>
                    {renderCompactScorecard(row.holeScores)}
                  </section>
                ))}
              </div>

              <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#d8d2c4] bg-[#f7f4ec] p-4">
                <FlagIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#9a7735]" />
                <div>
                  <h3 className="text-sm font-semibold text-[#183d2d]">
                    Kontrollera innan du skickar in
                  </h3>
                  <p className="mt-1 text-xs leading-5 text-[#68716c]">
                    När scorekortet skickas in sparas
                    totalscorerna i turneringen och bollen
                    låses. Administratören kan senare
                    öppna den igen vid en rättelse.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-2 border-t border-[#d8d2c4] bg-[#f7f4ec] p-3 sm:grid-cols-2 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={() =>
                  setReviewOpen(false)
                }
                disabled={isSubmitting}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-4 text-sm font-semibold text-[#68716c] transition hover:bg-white disabled:opacity-50"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Tillbaka och ändra
              </button>

              <button
                type="button"
                onClick={submitBall}
                disabled={isSubmitting}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 text-sm font-semibold text-white transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2Icon className="h-4 w-4" />
                )}
                {isSubmitting
                  ? "Skickar in…"
                  : "Godkänn och skicka in"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}